import { doc, getDoc, deleteDoc, query, where, collection, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase/config';
import type { OTPMethod } from './authentica';

// OTP send/verify now run SERVER-SIDE (Cloud Functions sendOtpCode/verifyOtpCode):
// the Authentica API key never reaches the browser, and verification is
// idempotent server-side, which fixes the double-verify race that used to
// clobber isVerified and block registration (register 400 otp-not-verified).

interface OTPRecord {
  phone: string;
  otp: string;
  createdAt: Date;
  expiresAt: Date;
  attempts: number;
  isVerified: boolean;
  otpMethod: OTPMethod;
  useAuthentica: boolean;
}

interface OtpCallableResult {
  success: boolean;
  error?: string;
  otpMethod?: OTPMethod;
  // Set by verifyOtpCode when the phone belongs to exactly one existing user:
  // a server-minted custom token for that user (login grant).
  token?: string;
  userId?: string;
}

export async function isPhoneAvailable(phoneNumber: string): Promise<{ available: boolean; error?: string }> {
  try {
    const usersQuery = query(
      collection(db, 'users'),
      where('phone', '==', phoneNumber)
    );
    const userSnapshot = await getDocs(usersQuery);

    if (!userSnapshot.empty) {
      return {
        available: false,
        error: 'Phone number is already registered'
      };
    }

    const phoneReservationQuery = query(
      collection(db, 'phones'),
      where('phone', '==', phoneNumber)
    );
    const phoneSnapshot = await getDocs(phoneReservationQuery);

    if (!phoneSnapshot.empty) {
      return {
        available: false,
        error: 'Phone number is already reserved'
      };
    }

    return { available: true };
  } catch (error) {
    console.error('Error checking phone availability:', error);
    return {
      available: false,
      error: 'Failed to check phone availability'
    };
  }
}

export async function isUserExists(phoneNumber: string): Promise<{ exists: boolean; error?: string }> {
  try {
    const usersQuery = query(
      collection(db, 'users'),
      where('phone', '==', phoneNumber)
    );
    const userSnapshot = await getDocs(usersQuery);

    if (userSnapshot.empty) {
      return {
        exists: false,
        error: 'No user found with this phone number'
      };
    }

    return { exists: true };
  } catch (error) {
    console.error('Error checking user existence:', error);
    return {
      exists: false,
      error: 'Failed to check user existence'
    };
  }
}

async function sendOtpServerSide(
  phoneNumber: string,
  purpose: 'register' | 'login',
  language: 'en' | 'ar'
): Promise<{ success: boolean; error?: string; otpMethod?: OTPMethod }> {
  try {
    const send = httpsCallable<
      { phone: string; purpose: 'register' | 'login'; language: string },
      OtpCallableResult
    >(functions, 'sendOtpCode');
    const { data } = await send({ phone: phoneNumber, purpose, language });
    return { success: !!data.success, error: data.error, otpMethod: data.otpMethod };
  } catch (e: any) {
    const msg = String(e?.message || '');
    if (msg.includes('phone-already-registered')) {
      return { success: false, error: 'Phone number is already registered' };
    }
    if (msg.includes('no-user-for-phone')) {
      return { success: false, error: 'No user found with this phone number' };
    }
    if (msg.includes('wait-before-resend')) {
      return { success: false, error: 'Please wait a minute before requesting a new code.' };
    }
    console.error('Error sending OTP:', e);
    return { success: false, error: 'Failed to send OTP' };
  }
}

export async function sendLoginOTP(
  phoneNumber: string,
  language: 'en' | 'ar' = 'en'
): Promise<{ success: boolean; error?: string; otpMethod?: OTPMethod }> {
  return sendOtpServerSide(phoneNumber, 'login', language);
}

export async function sendOTP(
  phoneNumber: string,
  language: 'en' | 'ar' = 'en'
): Promise<{ success: boolean; error?: string; otpMethod?: OTPMethod }> {
  return sendOtpServerSide(phoneNumber, 'register', language);
}

export async function verifyOTP(
  phoneNumber: string,
  otpCode: string
): Promise<{ success: boolean; error?: string; token?: string; userId?: string }> {
  try {
    const verify = httpsCallable<
      { phone: string; code: string },
      OtpCallableResult
    >(functions, 'verifyOtpCode');
    const { data } = await verify({ phone: phoneNumber, code: otpCode });
    if (data.success) {
      return data.token
        ? { success: true, token: data.token, userId: data.userId }
        : { success: true };
    }
    const errorMap: Record<string, string> = {
      'otp-not-found': 'OTP not found or expired',
      'otp-expired': 'OTP has expired',
      'too-many-attempts': 'Too many failed attempts. Please request a new OTP.',
      'invalid-code': 'Invalid OTP',
    };
    return { success: false, error: errorMap[data.error || ''] || data.error || 'Invalid OTP' };
  } catch (error) {
    console.error('Error verifying OTP:', error);
    return { success: false, error: 'Verification failed. Please try again.' };
  }
}

export async function isPhoneVerified(phoneNumber: string): Promise<boolean> {
  try {
    const otpDoc = await getDoc(doc(db, 'otpVerifications', phoneNumber));

    if (!otpDoc.exists()) {
      return false;
    }

    const otpRecord = otpDoc.data() as OTPRecord;
    const now = new Date();

    if (now > (otpRecord.expiresAt as any).toDate()) {
      return false;
    }

    return otpRecord.isVerified;
  } catch (error) {
    console.error('Error checking phone verification:', error);
    return false;
  }
}

export async function cleanupOTP(phoneNumber: string): Promise<void> {
  try {
    await deleteDoc(doc(db, 'otpVerifications', phoneNumber));
  } catch (error) {
    console.error('Error cleaning up OTP:', error);
  }
}

export async function resendOTP(
  phoneNumber: string,
  language: 'en' | 'ar' = 'en'
): Promise<{ success: boolean; error?: string; otpMethod?: OTPMethod }> {
  // Throttling is enforced server-side (one send per phone per 60s).
  return sendOTP(phoneNumber, language);
}
